import type { ChangeEvent } from "react";
import { Button, Form, Input, Popconfirm, Switch, Tabs, Tooltip } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { ActivityType } from "../../../../types";
import type { FormActivityUI } from "../utils";
import { useLessonForm } from "./LessonFormContext";
import PedagogicalPills from "./PedagogicalPills";
import BlockItem from "./BlockItem";
import ActivitySectionView from "./ActivitySectionView";
import AlternativesPane from "./AlternativesPane";

const { TextArea } = Input;

interface ActivityCardProps {
  activity: FormActivityUI;
  typeIndex: number;
  activityType: ActivityType;
}

const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  typeIndex,
  activityType,
}) => {
  const {
    lesson,
    addActivity,
    removeActivity,
    addBlock,
    removeBlock,
    handleActivityUpdate,
    handleManipulativesToggle,
    generatingSummaryActivityId,
    generatingMaterialsActivityId,
    handleGenerateSummary,
    handleGenerateMaterials,
  } = useLessonForm();

  const activityIndex = lesson.activities.findIndex(
    (a) => a.id === activity.id,
  );

  /**
   * SCRUM-795: la captura de la actividad, igual que antes del ticket. Se
   * extrae a una variable para poder mostrarla como pestaña junto a las
   * alternativas sin que su contenido cambie en nada.
   */
  const activityFields = (
    <>
      {/* Campos básicos */}
      <div className="activity-header">
        <Form.Item
          label="Título actividad"
          className="activity-title"
          rules={[
            {
              required: true,
              message: "El título de la actividad es obligatorio",
            },
          ]}
        >
          <Input
            placeholder="Título"
            value={activity.title}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleActivityUpdate(activityIndex, "title", e.target.value)
            }
          />
        </Form.Item>

        <Form.Item
          label="Tiempo de actividad (en minutos)"
          className="activity-duration"
        >
          <Input
            type="number"
            value={activity.duration}
            placeholder="Minutos"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleActivityUpdate(
                activityIndex,
                "duration",
                Number(e.target.value),
              )
            }
          />
        </Form.Item>

        <Form.Item label="¿Es una tarea?" className="activity-homework">
          <Switch
            checked={activity.is_homework || false}
            onChange={(checked) =>
              handleActivityUpdate(activityIndex, "is_homework", checked)
            }
          />
        </Form.Item>

        <Form.Item
          label="Actividad con manipulativos"
          className="activity-manipulatives"
        >
          <Tooltip
            title={
              activity.persisted_has_manipulatives
                ? "Esta actividad ya se guardó con manipulativos: no puede volver a ser una actividad regular."
                : "Activa esta opción para estructurar la actividad en 3 secciones: Demostración guiada, Manos a la obra y Exploración acompañada. Los materiales e instrucciones actuales se moverán al primer bloque de Demostración guiada. Una vez que guardes la lección no podrás desactivarla."
            }
          >
            <Switch
              checked={activity.has_manipulatives}
              disabled={activity.persisted_has_manipulatives}
              onChange={(checked) =>
                handleManipulativesToggle(activityIndex, checked)
              }
            />
          </Tooltip>
        </Form.Item>

        <Form.Item
          label="Actividad socioemocional"
          className="activity-socioemotional"
        >
          <Switch
            checked={activity.is_socioemotional || false}
            onChange={(checked) =>
              handleActivityUpdate(activityIndex, "is_socioemotional", checked)
            }
          />
        </Form.Item>
      </div>

      {/* Pastillas pedagógicas */}
      <PedagogicalPills activityIndex={activityIndex} />

      {/* Resumen de actividad */}
      <div className="activity-summary-container">
        <Form.Item
          label="Resumen de actividad"
          className="activity-summary"
          extra="Máximo 150 caracteres. Este resumen será visible para los profesores."
        >
          <div
            style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
          >
            <TextArea
              placeholder="Resumen breve de la actividad..."
              value={activity.summary || ""}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleActivityUpdate(activityIndex, "summary", e.target.value)
              }
              maxLength={150}
              showCount
              rows={3}
              style={{ flex: 1 }}
            />
            <Tooltip title="Generar resumen con IA">
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={generatingSummaryActivityId === activity.id}
                onClick={() => handleGenerateSummary(activityIndex)}
                style={{ minWidth: "40px" }}
              >
                {generatingSummaryActivityId === activity.id ? "" : "IA"}
              </Button>
            </Tooltip>
          </div>
        </Form.Item>
      </div>

      {/* Materiales manipulativos */}
      {activity.has_manipulatives && (
        <div className="activity-manipulative-materials-container">
          <Form.Item
            label="Materiales manipulativos"
            className="activity-manipulative-materials"
          >
            <div
              style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
            >
              <TextArea
                placeholder="Lista de materiales manipulativos..."
                value={activity.manipulative_materials_list || ""}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleActivityUpdate(
                    activityIndex,
                    "manipulative_materials_list",
                    e.target.value,
                  )
                }
                rows={3}
                style={{ flex: 1 }}
              />
              <Tooltip title="Generar materiales con IA">
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={generatingMaterialsActivityId === activity.id}
                  onClick={() => handleGenerateMaterials(activityIndex)}
                  style={{ minWidth: "40px" }}
                >
                  {generatingMaterialsActivityId === activity.id ? "" : "IA"}
                </Button>
              </Tooltip>
            </div>
          </Form.Item>
        </div>
      )}

      {/* Metas socioemocionales */}
      {activity.is_socioemotional && (
        <div className="activity-socioemotional-goals-container">
          <Form.Item
            label="Meta socioemocional principal"
            className="activity-socioemotional-main-goal"
          >
            <TextArea
              placeholder="Meta socioemocional principal..."
              value={activity.socioemotional_main_goal || ""}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleActivityUpdate(
                  activityIndex,
                  "socioemotional_main_goal",
                  e.target.value,
                )
              }
              rows={3}
            />
          </Form.Item>
          <Form.Item
            label="Meta socioemocional de apoyo"
            className="activity-socioemotional-support-goal"
          >
            <TextArea
              placeholder="Meta socioemocional de apoyo..."
              value={activity.socioemotional_support_goal || ""}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleActivityUpdate(
                  activityIndex,
                  "socioemotional_support_goal",
                  e.target.value,
                )
              }
              rows={3}
            />
          </Form.Item>
        </div>
      )}

      {/* Secciones o bloques */}
      {activity.has_manipulatives ? (
        activity.sections.map((section, sectionIndex) => (
          <ActivitySectionView
            key={sectionIndex}
            section={section}
            sectionIndex={sectionIndex}
            activityIndex={activityIndex}
          />
        ))
      ) : (
        <>
          {activity.blocks.map((block, blockIndex) => (
            <BlockItem
              key={block.id ?? blockIndex}
              block={block}
              blockIndex={blockIndex}
              activityIndex={activityIndex}
              totalBlocks={activity.blocks.length}
              onRemove={() => removeBlock(activityIndex, blockIndex)}
            />
          ))}
          <Button
            type="dashed"
            onClick={() => activity.id && addBlock(activity.id)}
            icon={<PlusOutlined />}
            className="add-block-button"
          >
            Añadir bloque
          </Button>
        </>
      )}
    </>
  );

  return (
    <div className="activity-section">
      {/* Header con acciones */}
      <div className="activity-header-with-actions">
        <h3>Actividad {typeIndex + 1}</h3>
        <div className="activity-actions">
          <Button
            type="dashed"
            onClick={() => addActivity(activityType, typeIndex - 1)}
            icon={<PlusOutlined />}
            className="add-before-button"
          >
            Añadir antes
          </Button>
          <Button
            type="dashed"
            onClick={() => addActivity(activityType, typeIndex)}
            icon={<PlusOutlined />}
            className="add-after-button"
          >
            Añadir después
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar esta actividad?"
            onConfirm={() => activity?.id && removeActivity(activity.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              className="delete-activity-button"
            />
          </Popconfirm>
        </div>
      </div>

      {/* SCRUM-795: las alternativas se capturan en su propia pestaña, que
          sólo existe para una actividad manipulativa. */}
      {activity.has_manipulatives ? (
        <Tabs
          className="activity-alternatives-tabs"
          items={[
            {
              key: "activity",
              label: "Actividad",
              children: activityFields,
            },
            {
              key: "alternatives",
              label: "Alternativas sin manipulativos",
              children: (
                <AlternativesPane
                  activity={activity}
                  activityIndex={activityIndex}
                />
              ),
            },
          ]}
        />
      ) : (
        activityFields
      )}
    </div>
  );
};

export default ActivityCard;
